import type {
  AdjustmentEntry,
  AnnotationRow,
  CoachCardRow,
  FlagResolutionRow,
  HypothesisRow,
  LogEntryRow,
  MatchNoteRow,
  MatchRow,
  ObservationRow,
  OurPlayerRow,
  PlayerRow,
  SetPieceFlags,
  SetPieceMarker,
  SetPieceRow,
  SettingKey,
  SettingRow,
  TalkPointRow,
  TeamRow,
  TemplateEntry,
  TemplateRow,
  TemplateSlot,
} from '../types';

/**
 * **Design §11.7's "static per-entity field-list table — data, not logic."**
 *
 * This file is the reason INV-12 is achievable. Every other determinism control in §11.7 protects
 * the *container*; this one protects the *content*, and it is the one a default would break most
 * quietly: `Object.keys` on a row does not return a stable order (V8 emits integer-like keys first,
 * then insertion order), so a row rebuilt by a spread, a Dexie read, or an import would serialize
 * its fields in a different sequence and the archive would hash differently for identical data.
 *
 * ── Three properties, each structural rather than remembered ─────────────────
 *
 * 1. **Order is declared.** The array order below *is* the serialization order. §3.1's declaration
 *    order is reproduced so the two can be diffed by eye.
 * 2. **Coverage is exhaustive, in both directions.** Each entity declares an `archived` list and an
 *    `omitted` list, and a compile-time assertion requires their union to equal `keyof Row` with an
 *    empty intersection. A field added to a row type therefore **breaks the build** until someone
 *    declares whether it is published. A one-way check would find missing fields and never find
 *    fields that should not have been published (`bidirectional-analysis`).
 * 3. **Every omission carries its reason at the row.** An unexplained omission is indistinguishable
 *    from an oversight six months later.
 *
 * ── The boundary this file sits on (§1.2) ────────────────────────────────────
 *
 * `archive/` may import `types/` and `config/` and **neither `domain/` nor `data/`**. That is what
 * makes §13.5's `VersionError` recovery screen able to export a real BAK-1 archive out of raw rows
 * when the schema is too new for this bundle to understand. `AudioBlobRow` is declared in `data/`
 * (§3.1's boundary rule — it is the one lib-typed row), so this file declares its own **structural**
 * metadata type instead; `src/test/boundary.test.ts` is where the two are checked against each other,
 * because a test may cross a layer boundary that production code may not.
 */

// ─────────────────────────────────────────────────────────────────────────────
// The exhaustiveness machinery
// ─────────────────────────────────────────────────────────────────────────────

type AssertTrue<T extends true> = T;

/**
 * True when `Archived ∪ Omitted === keyof Row` and `Archived ∩ Omitted === ∅`.
 *
 * The intersection half matters as much as the union half: a field listed in both would be archived
 * *and* documented as excluded, and the documentation is what a reader would trust.
 */
type Partitions<Row, Archived extends keyof Row, Omitted extends keyof Row> = [
  Exclude<keyof Row, Archived | Omitted>,
] extends [never]
  ? [Extract<Archived, Omitted>] extends [never]
    ? true
    : false
  : false;

/** Ordered field list for one entity. `readonly` so a caller cannot sort it into a different order. */
export type FieldList<Row> = readonly (keyof Row & string)[];

// ─────────────────────────────────────────────────────────────────────────────
// Team, Player  (SYN-4: "plus the Team and Player rows it references")
// ─────────────────────────────────────────────────────────────────────────────

export const TEAM_FIELDS = [
  'id',
  'name',
  'club',
  'ageGroup',
  'notes',
  'createdAt',
] as const satisfies FieldList<TeamRow>;

/** No omissions — every `Team` field is domain data VER-7 compares. */
const TEAM_OMITTED = [] as const satisfies FieldList<TeamRow>;

type _TeamPartitions = AssertTrue<
  Partitions<TeamRow, (typeof TEAM_FIELDS)[number], (typeof TEAM_OMITTED)[number]>
>;

/**
 * **ENV-11 lives here.** "The schema provides no field for a player's name or physical appearance.
 * Players are identified by shirt number only." This list is the last declaration before data leaves
 * the device, and `archive.env11.test.ts` walks every field name in this file against a
 * person-identifying vocabulary — so a `name` added to `PlayerRow` fails a test, not a review.
 */
export const PLAYER_FIELDS = [
  'id',
  'teamId',
  'number',
  'foot',
  'notes',
] as const satisfies FieldList<PlayerRow>;

const PLAYER_OMITTED = [] as const satisfies FieldList<PlayerRow>;

type _PlayerPartitions = AssertTrue<
  Partitions<PlayerRow, (typeof PLAYER_FIELDS)[number], (typeof PLAYER_OMITTED)[number]>
>;

// ─────────────────────────────────────────────────────────────────────────────
// Match
// ─────────────────────────────────────────────────────────────────────────────

export const MATCH_FIELDS = [
  'id',
  'teamId',
  'comp',
  'date',
  // PRE-8 / §11.7a — **immediately after `date`**, and that position is normative: `MatchRow`,
  // `MatchSchema` and this list declare one order so the three never have to be reconciled. Adding
  // it changes the canonical bytes of every archive, so the first drain after this version ships
  // re-PUTs each archive exactly once — §11.7 licenses that in as many words, and INV-12 is a
  // same-client property whose two sides move together.
  'format',
  // B §4.1 (F-2) — **immediately after `format`**, and that position is normative: `MatchRow`,
  // `MatchSchema` and this list declare one order. Adding it changes the canonical bytes of
  // every archive, which is (half of) why `ARCHIVE_FORMAT_VERSION` moved to 3.
  'purpose',
  'halfLengthMin',
  'formationGuess',
  'kickoffTs',
  'halfTs',
  'kickoff2Ts',
  'fullTimeTs',
  'state',
  'capturePhase',
  'lockTs',
  'abandoned',
  'triagePassedTs',
] as const satisfies FieldList<MatchRow>;

/**
 * **Sync bookkeeping is omitted from the bytes, and this is load-bearing beyond §17.4.**
 *
 * §17.4 excludes both from VER-7's restore comparison because they are written *after* the archive
 * is built and only on a 2xx. The stronger reason is §11.3: freshness is decided by comparing the
 * archive's hash against the server manifest. If `lastSyncedTs` were inside the archive, then every
 * successful push would change the next build's bytes, the hash would never match, **every drain
 * would re-PUT**, and §11.4's lost-response self-heal — which depends on a rebuild being
 * byte-identical to what was already stored — could never fire. One field would turn a bounded
 * three-pushes-per-match design (SYN-10) into an unbounded one.
 */
const MATCH_OMITTED = ['lastSyncedTs', 'lastSyncedHash'] as const satisfies FieldList<MatchRow>;

type _MatchPartitions = AssertTrue<
  Partitions<MatchRow, (typeof MATCH_FIELDS)[number], (typeof MATCH_OMITTED)[number]>
>;

// ─────────────────────────────────────────────────────────────────────────────
// Hypothesis, Observation, FlagResolution
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `status`, `statusOverride` and `cachedCount` are **all** archived, deliberately.
 *
 * §3.3: "The archive records both `status='disproved'` and `cachedCount=7` and
 * `statusOverride='disproved'`, so nothing is lost on disk and a later reversal is exact." Storing
 * only the derived `status` would make INV-3's reversal lossy across a restore; storing only the two
 * dimensions would make an importing older client re-derive a field it may derive differently.
 */
export const HYPOTHESIS_FIELDS = [
  'id',
  'matchId',
  'text',
  'phase',
  'discoveryBucket',
  'playerId',
  'statusOverride',
  'cachedCount',
  'lastIncrementTs',
  // §3.2 / `v002` — DISC-4's third sort term. Archived because a restored match must render in the
  // order it was captured in; dropping it would send every restored pre-kickoff board back to the
  // random `id asc` fall-through this column exists to remove.
  'createdAt',
  'status',
  'boardPosition',
  'sourceTemplateId',
  'carriedFromMatchId',
  'carriedStatus',
  'carriedCount',
] as const satisfies FieldList<HypothesisRow>;

const HYPOTHESIS_OMITTED = [] as const satisfies FieldList<HypothesisRow>;

type _HypothesisPartitions = AssertTrue<
  Partitions<HypothesisRow, (typeof HYPOTHESIS_FIELDS)[number], (typeof HYPOTHESIS_OMITTED)[number]>
>;

export const OBSERVATION_FIELDS = [
  'id',
  'hypothesisId',
  'matchId',
  'matchMinute',
  'kind',
  'audioBlobId',
  'createdAt',
] as const satisfies FieldList<ObservationRow>;

const OBSERVATION_OMITTED = [] as const satisfies FieldList<ObservationRow>;

type _ObservationPartitions = AssertTrue<
  Partitions<
    ObservationRow,
    (typeof OBSERVATION_FIELDS)[number],
    (typeof OBSERVATION_OMITTED)[number]
  >
>;

export const FLAG_RESOLUTION_FIELDS = [
  'id',
  'observationId',
  'action',
  'resolvedAt',
  'promotedHypothesisId',
] as const satisfies FieldList<FlagResolutionRow>;

const FLAG_RESOLUTION_OMITTED = [] as const satisfies FieldList<FlagResolutionRow>;

type _FlagResolutionPartitions = AssertTrue<
  Partitions<
    FlagResolutionRow,
    (typeof FLAG_RESOLUTION_FIELDS)[number],
    (typeof FLAG_RESOLUTION_OMITTED)[number]
  >
>;

// ─────────────────────────────────────────────────────────────────────────────
// SetPiece, and the two nested JSON shapes §3.5 keeps as columns
// ─────────────────────────────────────────────────────────────────────────────

export const SET_PIECE_FIELDS = [
  'id',
  'matchId',
  'type',
  'matchMinute',
  'scheme',
  'markers',
  'flags',
  'notes',
  'draft',
] as const satisfies FieldList<SetPieceRow>;

const SET_PIECE_OMITTED = [] as const satisfies FieldList<SetPieceRow>;

type _SetPiecePartitions = AssertTrue<
  Partitions<SetPieceRow, (typeof SET_PIECE_FIELDS)[number], (typeof SET_PIECE_OMITTED)[number]>
>;

/** SP-5 — the only floats in the schema. `canonical.ts` holds them to `GEOMETRY_DECIMALS`. */
export const SET_PIECE_MARKER_FIELDS = ['x', 'y', 'type'] as const satisfies FieldList<
  Required<SetPieceMarker>
>;

type _MarkerPartitions = AssertTrue<
  Partitions<SetPieceMarker, (typeof SET_PIECE_MARKER_FIELDS)[number], never>
>;

/**
 * Every key is optional on the row, so this list is an **order**, not a presence contract:
 * `canonical.ts` emits the declared keys that are present, in this sequence. Emitting absent keys as
 * `null` would change their meaning on import — `swing: null` is not the same fact as "no swing
 * recorded" — so presence is preserved and only order is imposed.
 */
export const SET_PIECE_FLAG_FIELDS = [
  'leftUpTop',
  'edgeMarked',
  'swing',
  'targetZone',
] as const satisfies FieldList<Required<SetPieceFlags>>;

type _FlagPartitions = AssertTrue<
  Partitions<Required<SetPieceFlags>, (typeof SET_PIECE_FLAG_FIELDS)[number], never>
>;

// ─────────────────────────────────────────────────────────────────────────────
// TalkPoint, MatchNote
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `TalkPointRow` is a discriminated union (INV-13), and the two variants have the same key set —
 * only `instructionText`'s nullability differs. One field list therefore serves both, and
 * `keyof TalkPointRow` (the intersection of the variants' keys) is the right thing to assert
 * against because it is exactly the set every variant is guaranteed to carry.
 */
export const TALK_POINT_FIELDS = [
  'id',
  'matchId',
  'phase',
  'rank',
  'instructionText',
  'sourceHypothesisId',
  'attachedSetPieceId',
] as const satisfies FieldList<TalkPointRow>;

const TALK_POINT_OMITTED = [] as const satisfies FieldList<TalkPointRow>;

type _TalkPointPartitions = AssertTrue<
  Partitions<TalkPointRow, (typeof TALK_POINT_FIELDS)[number], (typeof TALK_POINT_OMITTED)[number]>
>;

/** INV-14 — a note is archived (SYN-4 names it) and never reaches a count. */
export const MATCH_NOTE_FIELDS = [
  'id',
  'matchId',
  'phase',
  'text',
  'audioBlobId',
  'matchMinute',
  'createdAt',
  'updatedAt',
] as const satisfies FieldList<MatchNoteRow>;

const MATCH_NOTE_OMITTED = [] as const satisfies FieldList<MatchNoteRow>;

type _MatchNotePartitions = AssertTrue<
  Partitions<MatchNoteRow, (typeof MATCH_NOTE_FIELDS)[number], (typeof MATCH_NOTE_OMITTED)[number]>
>;

// ─────────────────────────────────────────────────────────────────────────────
// Build unit B-1 — Annotation, CoachCard (match-scoped), OurPlayer (profile)
// ─────────────────────────────────────────────────────────────────────────────

/** B §4.1 — scenario 17's notes. Every field is domain data; nothing to omit. */
export const ANNOTATION_FIELDS = [
  'id',
  'matchId',
  'hypothesisId',
  'text',
  'createdAt',
  'updatedAt',
] as const satisfies FieldList<AnnotationRow>;

const ANNOTATION_OMITTED = [] as const satisfies FieldList<AnnotationRow>;

type _AnnotationPartitions = AssertTrue<
  Partitions<AnnotationRow, (typeof ANNOTATION_FIELDS)[number], (typeof ANNOTATION_OMITTED)[number]>
>;

/**
 * B §4.1 — the adjustments-workflow card. `selectedHypothesisIds` is a scalar array (the
 * canonical writer serializes it element-wise); `adjustments` is the nested JSON column whose
 * per-entry key order is declared below, the `markers` pattern.
 */
export const COACH_CARD_FIELDS = [
  'id',
  'matchId',
  'context',
  'status',
  'selectedHypothesisIds',
  'adjustments',
  'generatedAt',
  'finalizedAt',
  'createdAt',
  'updatedAt',
] as const satisfies FieldList<CoachCardRow>;

const COACH_CARD_OMITTED = [] as const satisfies FieldList<CoachCardRow>;

type _CoachCardPartitions = AssertTrue<
  Partitions<CoachCardRow, (typeof COACH_CARD_FIELDS)[number], (typeof COACH_CARD_OMITTED)[number]>
>;

/**
 * One adjustment entry — every key is required on the type, so unlike `SetPieceFlags` this list
 * is a presence contract as well as an order; a valid entry emits all eight keys in this order.
 */
export const ADJUSTMENT_ENTRY_FIELDS = [
  'entryId',
  'text',
  'phase',
  'sourceHypothesisIds',
  'origin',
  'status',
  'aiOriginalText',
  'createdAt',
] as const satisfies FieldList<AdjustmentEntry>;

type _AdjustmentEntryPartitions = AssertTrue<
  Partitions<AdjustmentEntry, (typeof ADJUSTMENT_ENTRY_FIELDS)[number], never>
>;

/**
 * B §4.1, reshaped by §15.3 — our roster.
 *
 * **This list changing completely is what bumps `ARCHIVE_FORMAT_VERSION` 3 → 4** (`constants.ts`
 * sets the criterion: what decides the number is whether a *published* field list changes).
 *
 * `firstName` is a person-identifying field carried deliberately, under ruling **R-1**: ENV-11
 * governs *opponent* players, third parties the user is scouting, while his own squad is a
 * different population. `ALLOWED_NAME_FIELDS` names `ourPlayers.firstName` for exactly this
 * reason, and `archive/exclusions.test.ts`'s vocabulary sweep runs over these names like every
 * other field in this file — it is re-aimed at the ruling in force, never weakened.
 *
 * `ratings` is a sparse nested object and needs **no** `NESTED_FIELD_ORDERS` entry:
 * `canonical.ts` emits an undeclared nested object by `sortedKeys`, and the null-materialising
 * path (`nullMissing`) applies to top-level entity fields only — so an unrated attribute stays
 * *absent* from the bytes and never emits `"someKey": null`.
 */
export const OUR_PLAYER_FIELDS = [
  'id',
  'jerseyNumber',
  'firstName',
  'primaryPosition',
  'secondaryPositions',
  'ratings',
  'active',
  'createdAt',
  'updatedAt',
] as const satisfies FieldList<OurPlayerRow>;

const OUR_PLAYER_OMITTED = [] as const satisfies FieldList<OurPlayerRow>;

type _OurPlayerPartitions = AssertTrue<
  Partitions<OurPlayerRow, (typeof OUR_PLAYER_FIELDS)[number], (typeof OUR_PLAYER_OMITTED)[number]>
>;

// ─────────────────────────────────────────────────────────────────────────────
// Template and its nested entries (the profile archive's first half)
// ─────────────────────────────────────────────────────────────────────────────

export const TEMPLATE_FIELDS = [
  'id',
  'name',
  'phase',
  'hypothesisTexts',
  'builtin',
] as const satisfies FieldList<TemplateRow>;

const TEMPLATE_OMITTED = [] as const satisfies FieldList<TemplateRow>;

type _TemplatePartitions = AssertTrue<
  Partitions<TemplateRow, (typeof TEMPLATE_FIELDS)[number], (typeof TEMPLATE_OMITTED)[number]>
>;

/** `slots` is optional on the entry — present-keys-only, same rule as `SetPieceFlags`. */
export const TEMPLATE_ENTRY_FIELDS = [
  'text',
  'phase',
  'discoveryBucket',
  'slots',
] as const satisfies FieldList<Required<TemplateEntry>>;

type _TemplateEntryPartitions = AssertTrue<
  Partitions<Required<TemplateEntry>, (typeof TEMPLATE_ENTRY_FIELDS)[number], never>
>;

export const TEMPLATE_SLOT_FIELDS = ['key', 'kind'] as const satisfies FieldList<TemplateSlot>;

type _TemplateSlotPartitions = AssertTrue<
  Partitions<TemplateSlot, (typeof TEMPLATE_SLOT_FIELDS)[number], never>
>;

// ─────────────────────────────────────────────────────────────────────────────
// Setting — §11.8's exclusion, as a closed decision rather than a remembered list
// ─────────────────────────────────────────────────────────────────────────────

export const SETTING_FIELDS = ['key', 'value'] as const satisfies FieldList<SettingRow>;

const SETTING_OMITTED = [] as const satisfies FieldList<SettingRow>;

type _SettingPartitions = AssertTrue<
  Partitions<SettingRow, (typeof SETTING_FIELDS)[number], (typeof SETTING_OMITTED)[number]>
>;

/** What happens to one setting row when an archive is built. */
export type SettingDisposition = 'archive' | 'deny';

/**
 * **§11.8 — `backupCredential` is excluded from EVERY archive: the BAK-1 export and the SYN-4
 * profile archive alike.**
 *
 * From the export because that file gets shared (§12.1). From the profile archive because pushing a
 * credential to the service it authenticates is circular, and because SYN-5 restore runs *before* a
 * credential could be used to fetch it — the user re-enters it once in Settings, exactly as at first
 * setup (DD-4), and "Test connection" confirms it.
 *
 * **Why this is a `Record<SettingKey, …>` and not an array of denied names.** A deny-list over an
 * open key space is a list somebody has to remember; the failure mode is silent and it publishes a
 * secret. Because `SettingKey` is a closed union in `types/`, this map is exhaustive-checked: adding
 * a setting **does not compile** until its disposition is declared. And `settingDisposition()`
 * below denies anything it does not recognise, so a row that reached IndexedDB around the type
 * (a corrupt row, an archive from a newer client) is excluded rather than published by default.
 * Both halves are needed: the compile check makes new keys a decision, the runtime default makes
 * unknown keys safe.
 */
export const SETTING_DISPOSITION = {
  lockNudgeMin: 'archive',
  audioTickOn: 'archive',
  backupCredential: 'deny',
  lastSeenVersion: 'archive',
  /**
   * B spec §4.2 (F-5, build unit B-4) — denied for a different reason than the credential: not a
   * secret but **device-local by declaration**. A theme is a property of the device's viewing
   * conditions, not of the data set, so an archive restored onto another phone must not carry the
   * old phone's display choice with it. Denied in both directions, like everything on this list:
   * never exported, and stripped by the import's security pass (`archive/import.ts`).
   */
  themePreference: 'deny',
  /**
   * B spec §15.4 (ruling **R-4**, build unit B-25) — **archived**, and the contrast with
   * `themePreference` directly above is the clean test for why.
   *
   * A theme is a fact about *this phone's viewing conditions*; a 3-3-2 is a fact about *this team*.
   * The shape is content in the same category as `ourPlayers`, and a restore that brought back
   * fifteen players' ratings while dropping the shape they play would return **half a profile**.
   *
   * `canonical.ts` already serialises `Setting.value` byte-deterministically by `sortedKeys`
   * (`:70-77,123-133`), so a structured value needs no `NESTED_FIELD_ORDERS` entry and no new code
   * to travel — which is the whole of what makes R-4 cheaper than a `teamShape` table.
   */
  teamShape: 'archive',
} as const satisfies Record<SettingKey, SettingDisposition>;

/**
 * Deny-by-default lookup for a key that may not be a declared `SettingKey` at runtime.
 *
 * Data crossing this function has come off disk, and IndexedDB does not enforce TypeScript.
 */
export function settingDisposition(key: string): SettingDisposition {
  return (SETTING_DISPOSITION as Record<string, SettingDisposition | undefined>)[key] ?? 'deny';
}

/**
 * **A key this format has DECLARED must never be archived** — as distinct from one it has never
 * heard of. Build unit U-6 needed the difference; the two directions are not symmetric.
 *
 * *Writing* an archive, unknown must mean deny: a key whose sensitivity this bundle cannot know must
 * not be published by this bundle, so `settingDisposition()` is deny-by-default and that is correct.
 *
 * *Reading* one, the same rule would conflate two different events. A `backupCredential` inside an
 * archive means the file was produced by something that did not apply §11.8 — it is a **security**
 * finding, and the value must be dropped before it reaches a row validator, let alone a row. An
 * unrecognised key means a **newer client** wrote a preference this bundle has no name for — a
 * forward-compatibility finding, whose right answer is the repo-boundary quarantine (which reports
 * it by name so the user knows which preference was lost) rather than a silent security strip under
 * an alarming label.
 *
 * So the import strips what this predicate matches, and lets everything else meet the schema.
 */
export function isDeniedSettingKey(key: string): boolean {
  return (
    Object.prototype.hasOwnProperty.call(SETTING_DISPOSITION, key) &&
    settingDisposition(key) === 'deny'
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AudioBlob — the one entity whose row type this layer may not import (§3.1)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Audio metadata as the archive carries it. **Structural, not imported.**
 *
 * `AudioBlobRow` is declared in `data/db.ts` because `AudioBlob.blob` is the schema's only lib-typed
 * field and `types/` is compiled without DOM libs (§3.1's boundary rule). `archive/` may not import
 * `data/`, so it declares the shape it needs and `AudioBlobRow` satisfies it structurally — the
 * bytes arrive separately, through `ArchiveBlobReader`.
 *
 * `blob` and `chunks` are absent by construction rather than by omission: the bytes are a **file**
 * in the zip (`blobs/<id>.<ext>`), not a JSON field, and `chunks` is a pre-finalize staging area
 * that AUD-1's verbatim assembly consumes. An archive of an unfinalized recording carries
 * `complete: false` and whatever bytes exist, which is what §8.5's recovery pass would have found.
 */
export interface ArchiveAudioBlobMeta {
  id: string;
  mime: string;
  bytes: number;
  complete: boolean;
  createdAt: number;
}

export const AUDIO_BLOB_FIELDS = [
  'id',
  'mime',
  'bytes',
  'complete',
  'createdAt',
] as const satisfies FieldList<ArchiveAudioBlobMeta>;

type _AudioBlobPartitions = AssertTrue<
  Partitions<ArchiveAudioBlobMeta, (typeof AUDIO_BLOB_FIELDS)[number], never>
>;

/**
 * MIME → file extension for `blobs/<id>.<ext>`. **Data, and cosmetic.**
 *
 * The authoritative MIME is the `mime` field in `data.json`; this exists so a user who unzips a
 * BAK-1 export can double-click a recording. Import therefore keys on the basename before the first
 * `.` (a ULID contains no `.`), never on the extension — otherwise adding a row here would change
 * how old archives are read.
 */
const MIME_EXTENSIONS: Readonly<Record<string, string>> = {
  'audio/mp4': 'm4a',
  'audio/aac': 'aac',
  'audio/mpeg': 'mp3',
  'audio/ogg': 'ogg',
  'audio/wav': 'wav',
  'audio/webm': 'webm',
};

/** Unknown or empty MIME (an unfinalized recording has `mime: ''`) falls back to `bin`. */
export const DEFAULT_BLOB_EXTENSION = 'bin';

export function blobExtension(mime: string): string {
  const base = (mime.split(';')[0] ?? '').trim().toLowerCase();
  return MIME_EXTENSIONS[base] ?? DEFAULT_BLOB_EXTENSION;
}

// ─────────────────────────────────────────────────────────────────────────────
// Entity order — the order `data.json` and `manifest.entityCounts` emit
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The archive's entity vocabulary, in emission order.
 *
 * Referential order (teams before players before matches before their dependents) rather than
 * alphabetical, so a human reading `data.json` reads it top-down and an importer can walk it in
 * order without a dependency sort.
 */
export const ARCHIVE_ENTITIES = [
  'teams',
  'players',
  'matches',
  'hypotheses',
  'observations',
  'flagResolutions',
  'setPieces',
  'talkPoints',
  'matchNotes',
  // B-1 (format version 3): the two match-scoped B entities sit with the other match
  // dependents, referentially after the hypotheses they cite; the profile-scoped roster sits
  // last, after the other profile entities — it references nothing and nothing references it.
  'annotations',
  'coachCards',
  'audioBlobs',
  'templates',
  'settings',
  'ourPlayers',
] as const;

export type ArchiveEntity = (typeof ARCHIVE_ENTITIES)[number];

/**
 * Per-entity serialization spec: the ordered field list, and the field rows sort by.
 *
 * "Rows sorted by id (ULID)" (§11.7) is true of every entity except `settings`, which has no `id` —
 * its primary key is `key`. Naming the sort field per entity rather than hard-coding `'id'` is what
 * keeps that from being a special case buried in the serializer.
 */
export interface EntitySpec {
  readonly fields: readonly string[];
  readonly sortBy: string;
}

export const ENTITY_SPECS = {
  teams: { fields: TEAM_FIELDS, sortBy: 'id' },
  players: { fields: PLAYER_FIELDS, sortBy: 'id' },
  matches: { fields: MATCH_FIELDS, sortBy: 'id' },
  hypotheses: { fields: HYPOTHESIS_FIELDS, sortBy: 'id' },
  observations: { fields: OBSERVATION_FIELDS, sortBy: 'id' },
  flagResolutions: { fields: FLAG_RESOLUTION_FIELDS, sortBy: 'id' },
  setPieces: { fields: SET_PIECE_FIELDS, sortBy: 'id' },
  talkPoints: { fields: TALK_POINT_FIELDS, sortBy: 'id' },
  matchNotes: { fields: MATCH_NOTE_FIELDS, sortBy: 'id' },
  annotations: { fields: ANNOTATION_FIELDS, sortBy: 'id' },
  coachCards: { fields: COACH_CARD_FIELDS, sortBy: 'id' },
  audioBlobs: { fields: AUDIO_BLOB_FIELDS, sortBy: 'id' },
  templates: { fields: TEMPLATE_FIELDS, sortBy: 'id' },
  settings: { fields: SETTING_FIELDS, sortBy: 'key' },
  ourPlayers: { fields: OUR_PLAYER_FIELDS, sortBy: 'id' },
} as const satisfies Record<ArchiveEntity, EntitySpec>;

/**
 * Every field this format deliberately does **not** publish, by entity.
 *
 * The exhaustiveness assertions above already make an undeclared field a compile error; this export
 * is the readable half — a test asserts against it, and a reader who wants to know "is
 * `lastSyncedHash` in the archive?" gets an answer without inferring it from an absence.
 * `audioBlobs` is empty because its two excluded fields (`blob`, `chunks`) are not fields of
 * `ArchiveAudioBlobMeta` at all: the bytes are a zip entry, and `chunks` is pre-finalize staging.
 */
export const OMITTED_FIELDS = {
  teams: TEAM_OMITTED,
  players: PLAYER_OMITTED,
  matches: MATCH_OMITTED,
  hypotheses: HYPOTHESIS_OMITTED,
  observations: OBSERVATION_OMITTED,
  flagResolutions: FLAG_RESOLUTION_OMITTED,
  setPieces: SET_PIECE_OMITTED,
  talkPoints: TALK_POINT_OMITTED,
  matchNotes: MATCH_NOTE_OMITTED,
  annotations: ANNOTATION_OMITTED,
  coachCards: COACH_CARD_OMITTED,
  audioBlobs: [],
  templates: TEMPLATE_OMITTED,
  settings: SETTING_OMITTED,
  ourPlayers: OUR_PLAYER_OMITTED,
} as const satisfies Record<ArchiveEntity, readonly string[]>;

/**
 * Nested object shapes with a declared key order, keyed by the field that holds them.
 *
 * Reached by *field name* rather than by type, because the canonical writer walks values it has no
 * type information about. A nested shape absent from this map is serialized by the sorted-key rule
 * for opaque JSON — deterministic, just not human-ordered.
 */
export const NESTED_FIELD_ORDERS: Readonly<Record<string, readonly string[]>> = {
  markers: SET_PIECE_MARKER_FIELDS,
  flags: SET_PIECE_FLAG_FIELDS,
  hypothesisTexts: TEMPLATE_ENTRY_FIELDS,
  slots: TEMPLATE_SLOT_FIELDS,
  // B-1: each entry of `CoachCard.adjustments` emits its keys in this declared order (§4.1).
  adjustments: ADJUSTMENT_ENTRY_FIELDS,
};

// ─────────────────────────────────────────────────────────────────────────────
// OBS-1 log entries — not an entity; one NDJSON line each, BAK-1 exports only
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `seq` is omitted: it is the ring buffer's local auto-increment, not content.
 *
 * §17.4 already excludes the ring from VER-7's comparison because "the import writes log entries to
 * it *while importing* — it is mutated by the act of reading it", so a device-local index has
 * nothing to be compared against on the far side. Keeping it would also make every declared-field
 * `null` substitution fire, since `LogEntryRow.seq` is optional.
 */
export const LOG_ENTRY_FIELDS = [
  'ts',
  'level',
  'module',
  'matchId',
  'msg',
  'data',
] as const satisfies FieldList<LogEntryRow>;

type _LogEntryPartitions = AssertTrue<
  Partitions<LogEntryRow, (typeof LOG_ENTRY_FIELDS)[number], 'seq'>
>;

/**
 * **The build-failing half of this file, in one place.**
 *
 * Each member is a per-entity `Partitions<…>` assertion declared beside its entity above; this
 * aggregate is what references them, so `noUnusedLocals` keeps them alive. Purely type-level — the
 * emitted bundle contains nothing for it. Add a field to any row type and the tuple member for that
 * entity resolves to `false`, failing `AssertTrue` and naming the entity in the error.
 */
export type ArchiveFieldTableIsExhaustive = [
  _TeamPartitions,
  _PlayerPartitions,
  _MatchPartitions,
  _HypothesisPartitions,
  _ObservationPartitions,
  _FlagResolutionPartitions,
  _SetPiecePartitions,
  _MarkerPartitions,
  _FlagPartitions,
  _TalkPointPartitions,
  _MatchNotePartitions,
  _TemplatePartitions,
  _TemplateEntryPartitions,
  _TemplateSlotPartitions,
  _SettingPartitions,
  _AudioBlobPartitions,
  _LogEntryPartitions,
  _AnnotationPartitions,
  _CoachCardPartitions,
  _AdjustmentEntryPartitions,
  _OurPlayerPartitions,
];

/**
 * Tables that are **never** in any archive, each with the reason §17.4 gives.
 *
 * Exported as data so `serialize.ts` cannot accidentally acquire one, and so a test can assert the
 * union of archived entities and this list covers every table in `data/db.ts`'s `TABLE` — the
 * bidirectional check that catches a *new* table nobody decided about.
 */
export const NEVER_ARCHIVED_TABLES: Readonly<Record<string, string>> = {
  outbox: 'Sync bookkeeping local to one device; a restored profile has no counterpart queue (§17.4).',
  uiSession: 'Which screen a device was on. Not domain data, and meaningless after a restore (§17.4).',
  activeMatch:
    'Carries writerSessionId and a heartbeat, both identifying a browsing context that no longer exists (§17.4, §13.7).',
  logEntries:
    'The OBS-1 ring is not a JSON entity: it ships as debug/log.ndjson, and only in a BAK-1 (scope=all) export. Importing it would mutate the thing being read (§17.4).',
};
